import { useTracking } from "utilities/analytics/analytics";

import {
    BTDropdown,
    type IBTDropdownProps,
} from "commonComponents/btWrappers/BTDropdown/BTDropdown";
import { BTIconCaretSmallDown } from "commonComponents/btWrappers/BTIcon";
import { BTMenu } from "commonComponents/btWrappers/BTMenu/BTMenu";

import { BTButton, type BTButtonType, type IBTButtonProps } from "../BTButton/BTButton";

declare type OverlayFunc = () => React.ReactElement;

interface IBTDropdownActionsProps<ActionType> extends Omit<IBTDropdownProps, "overlay" | "testid"> {
    "data-testid": string;
    type?: BTButtonType;
    isolated?: boolean;
    actionBeingPerformed?: ActionType;
    loadingAction?: ActionType;
    /** The props for the dropdown button itself */
    buttonProps?: Partial<IBTButtonProps<ActionType>>;
    /** Menu items are used to populate the dropdown menu. If used and only one BTDropdownMenuItem is passed in, the dropdown will be rendered as a button instead */
    menuItems?: BTDropdownMenuItem[];
    /** If you want to render a custom dropdown overlay instead of a menu generated from `menuItems`, provide the overlay here. Note that if `menuItems` is provided, it will take precedence over `overlay` and the `overlay` prop will be ignored. */
    overlay?: React.ReactElement | OverlayFunc;
    /**
     * Should the caret be shown after the dropdown
     * @default true
     */
    showCaret?: boolean;
    onClick?: (e: React.MouseEvent) => void;
}

export class BTDropdownMenuItem {
    "data-testid": string;
    onClick: () => Promise<void> | void;
    disabled?: boolean;
    content: JSX.Element | string;
    "aria-label"?: string;
}

export function BTDropdownButton<ActionType>(props: IBTDropdownActionsProps<ActionType>) {
    const {
        children,
        type = "secondary",
        isolated,
        actionBeingPerformed,
        loadingAction,
        "data-testid": dataTestId,
        buttonProps,
        overlay,
        menuItems,
        showCaret = true,
        ...otherProps
    } = props;

    const { Track } = useTracking({
        component: "Dropdown Actions",
        uniqueId: props["data-testid"],
    });

    if (menuItems && menuItems.length === 1) {
        const menuItem = menuItems[0];
        return (
            <BTButton
                data-testid={menuItem["data-testid"]}
                onClick={menuItem.onClick}
                className={otherProps.className}
                {...(buttonProps || {})}
                disabled={menuItem.disabled}
                type={type}
                loadingAction={loadingAction}
                actionBeingPerformed={actionBeingPerformed}
            >
                {menuItem.content}
            </BTButton>
        );
    }

    let menuItemOverlay = null;
    if (menuItems) {
        menuItemOverlay = (
            <BTMenu testid="dropdownActionsMenu">
                {menuItems.map((x) => (
                    <BTMenu.Item
                        key={x["data-testid"]}
                        onClick={x.onClick}
                        testid={x["data-testid"]}
                        disabled={x.disabled}
                        aria-label={x["aria-label"]}
                    >
                        {x.content}
                    </BTMenu.Item>
                ))}
            </BTMenu>
        );
    }

    let buttonLabel: React.ReactNode = undefined;
    if (children || showCaret) {
        buttonLabel = (
            <>
                {children}
                {showCaret && <BTIconCaretSmallDown />}
            </>
        );
    }

    const dropdownButton = (
        <>
            <BTDropdown {...otherProps} testid={dataTestId} overlay={menuItemOverlay ?? overlay!}>
                <BTButton
                    disabled={otherProps.disabled}
                    actionBeingPerformed={actionBeingPerformed}
                    type={type}
                    data-testid={dataTestId}
                    loadingAction={loadingAction}
                    {...(buttonProps || {})}
                >
                    {buttonLabel}
                </BTButton>
            </BTDropdown>
        </>
    );

    return <Track>{dropdownButton}</Track>;
}
